import { lazy, type ComponentType, type LazyExoticComponent } from 'react';
import type { Session } from '../core/auth/AuthService';
import type { IconName } from '../design/Icon';

/** Ce que la coque transmet aux composants qu'un service fournit pour l'Accueil et la sidebar. */
export interface ServiceSlotProps {
  /** `null` en mode démonstration. */
  session: Session | null;
}

export type ServiceSlot = LazyExoticComponent<ComponentType<ServiceSlotProps>>;

/**
 * Un service de la plateforme vu par la coque. Ajouter un service = ajouter une entrée à `SERVICES` : la coque
 * (sidebar, barre d'onglets, grille Apps) ne change pas. Sans `path`, le service n'est pas encore disponible
 * dans le client : la coque n'affiche que son icône (aucun écran, aucune route).
 */
export interface ServiceDef {
  id: string;
  label: string;
  icon: IconName;
  path?: string;
  /** Principal : présent dans la sidebar et la barre d'onglets mobile ; sinon seulement dans la grille Apps. */
  primary?: boolean;
  /** La sidebar globale se réduit seule en languette sur les écrans de ce service (il a sa propre barre latérale). */
  compactSidebar?: boolean;
  /** Pastille de compteur de la sidebar. */
  badge?: ServiceSlot;
  /** Carte résumé de l'Accueil, quand le service fournit de vraies données. */
  summary?: ServiceSlot;
}

export const HOME: ServiceDef = { id: 'home', label: 'Accueil', icon: 'home', path: '/' };

export const SERVICES: readonly ServiceDef[] = [
  {
    id: 'passwords',
    label: 'Mots de passe',
    icon: 'vaultkey',
    path: '/vault',
    primary: true,
    compactSidebar: true,
    badge: lazy(() => import('../modules/vault/badge')),
    summary: lazy(() => import('../modules/vault/home')),
  },
  {
    id: 'notes',
    label: 'Notes',
    icon: 'check',
    path: '/notes',
    primary: true,
    compactSidebar: true,
    badge: lazy(() => import('../modules/notes/badge')),
    summary: lazy(() => import('../modules/notes/home')),
  },
  { id: 'treasury', label: 'Trésorerie', icon: 'card' },
  { id: 'canvas', label: 'Canvas', icon: 'doc' },
];

export const PRIMARY_SERVICES = SERVICES.filter((s) => s.primary);
export const isAvailable = (service: ServiceDef): boolean => service.path !== undefined;

export function isActivePath(pathname: string, path: string | undefined): boolean {
  if (!path) return false;
  return path === '/' ? pathname === '/' : pathname === path || pathname.startsWith(`${path}/`);
}

/** Vrai si le service affiché à cette adresse demande une sidebar globale réduite. */
export const wantsCompactSidebar = (pathname: string): boolean =>
  SERVICES.some((s) => s.compactSidebar && isActivePath(pathname, s.path));
